/*
PW Pane Splitter

Wrap around exactly two child elements to create a user-resizable division between them

Options (props)
- detached [bool]: If true, splitter will ignore child elements and only export dimension updates to a subscribed owner component
- onSplitterUpdated [func]: Function called when the splitter position updates. only used if detached=true
- placeholder [bool]: if true (if attribute/prop is included) the panes will not resize realtime, but will resize once when user "drops" the drag bar
- baseSide [string(top|right|bottom|left)]: base side to use as "zero point" when calculating offsets and min/max values
- initialOffset [int]: initial position of the division, measured in pixels from the supplied baseSide
- maxOffset [int]: maximum position of the division, measured in pixels from the supplied baseSide
- minOffset [int]: minimum position of the division, measured in pixels from the supplied baseSide

example configuration:
  <PaneSplitter baseSide="bottom" placeholder initialOffset={280} maxOffset={380} minOffset={180}>
    <div></div>
    <div></div>
  </PaneSplitter>
*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import cx from 'classnames'


// CSS properties that should receive dynamic offset values derived from splitter position
const OFFSET_STYLE_PROPS = {
  top: {
    a: 'height',
    b: 'top',
    bar: 'top'
  },
  bottom: {
    a: 'bottom',
    b: 'height',
    bar: 'bottom'
  },
  left: {
    a: 'width',
    b: 'left',
    bar: 'left'
  },
  right: {
    a: 'right',
    b: 'width',
    bar: 'right'
  }
}

const DEFAULT_INITIAL_OFFSET = 200

class PaneSplitter extends React.Component {
  static displayName = 'PaneSplitter'

  static propTypes = {
    onSplitterUpdated: T.func,
    detached: T.bool,
    placeholder: T.bool,
    baseSide: T.string.isRequired,
    initialOffset: T.number,
    maxOffset: T.number,
    minOffset: T.number,
    snapButtons: T.bool
  }

  static defaultProps = {
    baseSide: 'top',
    placeholder: false,
    detached: false,
    snapButtons: false
  }

  constructor(props) {
    super(props)
    const initialSpliterOffset = this.props.initialOffset
      ? this.props.initialOffset
      : DEFAULT_INITIAL_OFFSET
    this.state = {
      isActive: false,
      isDisabled: false,
      splitterOffset: initialSpliterOffset,
      placeholderOffset: initialSpliterOffset,
      baseOffset: 0
    }
  }

  UNSAFE_componentWillMount() {
    const props = this.props
    if (!props.detached && props.children && props.children.length !== 2) {
      // verify that component has 2 children. Only supports 2 panes right now
      console.warn(
        'Hey! PW React PaneSplitter should contain exactly two child nodes unless used in [detached] mode'
      )
    }
  }

  componentDidMount() {
    const props = this.props
    const callback = props.onSplitterUpdated
    if (callback && props.detached) {
      // TODO: if the consuming component calls setState within the callback, it can result in strange
      // lifecycle behavior such as other children getting componentDidUpdate fired before componentDidMount.
      // Delaying the callback blindly results in other bugs. Need to investigate further.
      callback({
        a: this.getPaneStyles('a'),
        b: this.getPaneStyles('b')
      })
    }
  }

  bindMoveEvents = () => {
    document.addEventListener('mousemove', this.onSplitterMove, false)
    document.addEventListener('touchmove', this.onSplitterMove, false)
    document.addEventListener('mouseup', this.onSplitterDrop, false)
    document.addEventListener('touchend', this.onSplitterDrop, false)
  }

  unbindMoveEvents = () => {
    document.removeEventListener('mousemove', this.onSplitterMove, false)
    document.removeEventListener('touchmove', this.onSplitterMove, false)
    document.removeEventListener('mouseup', this.onSplitterDrop, false)
    document.removeEventListener('touchend', this.onSplitterDrop, false)
  }

  onSplitterClick = () => {
    const node = ReactDOM.findDOMNode(this)
    const pos = node.getBoundingClientRect()

    var newBaseOffset
    switch (this.props.baseSide) {
      case 'top':
        newBaseOffset = pos.top
        break
      case 'bottom':
        newBaseOffset = pos.top + pos.height
        break
      case 'left':
        newBaseOffset = pos.left
        break
      case 'right':
        newBaseOffset = pos.left + pos.width
        break
    }

    this.setState({
      baseOffset: newBaseOffset, //update once on first click
      isActive: true
    })

    this.bindMoveEvents()
  }

  onSplitterMove = (e) => {
    const newPos = this.getSplitterPosition(e)
    if (this.props.placeholder) {
      this.setState({ placeholderOffset: newPos })
    } else {
      this.setState({ splitterOffset: newPos })
    }
    e.preventDefault()
    e.stopPropagation()
  }

  getSplitterPosition = (e) => {
    const { state, props } = this
    var newOffset
    switch (props.baseSide) {
      case 'top':
        newOffset = e.pageY - state.baseOffset
        break
      case 'bottom':
        newOffset = state.baseOffset - e.pageY
        break
      case 'left':
        newOffset = e.pageX - state.baseOffset
        break
      case 'right':
        newOffset = state.baseOffset - e.pageX
        break
    }
    if (props.minOffset && newOffset <= props.minOffset) {
      newOffset = props.minOffset //set to min
    }
    if (props.maxOffset && newOffset >= props.maxOffset) {
      newOffset = props.maxOffset //set to max
    }
    return newOffset
  }

  _snap = (isMax) => {
    const { minOffset, maxOffset, baseSide } = this.props
    const maxBound =
      baseSide === 'right' || baseSide === 'top' ? minOffset : maxOffset
    const minBound =
      baseSide === 'right' || baseSide === 'top' ? maxOffset : minOffset
    const val = isMax ? maxBound : minBound
    this.setState({
      placeholderOffset: val,
      splitterOffset: val
    })
    this._triggerUpdate(val)
  }

  onSplitterDrop = (e) => {
    const { props } = this
    this.unbindMoveEvents()

    if (props.placeholder) {
      // Placeholder was being moved the whole time, now need to update the panes and real splitter
      this.setState({ splitterOffset: this.getSplitterPosition(e) })
    }

    this.setState({
      isActive: false
    })

    // dispatch resize event.
    // TODO should this call a method passed into props instead? this seems like a shotgun approach...
    this._triggerUpdate()
  }

  _triggerUpdate = (offset) => {
    window.dispatchEvent(new Event('resize'))
    if (this.props.detached && this.props.onSplitterUpdated) {
      this.props.onSplitterUpdated({
        a: this.getPaneStyles('a', null, offset),
        b: this.getPaneStyles('b', null, offset)
      })
    }
  }

  _onKeyDown = e => {
    const {baseSide} = this.props
    const regex = /left|right/.test(baseSide) ? /(Left|Right)$/ : /(Up|Down)$/
    if (regex.test(e.key)) {
      const offset = this.state.splitterOffset + (10 *
        (/Left|Up/.test(e.key) ? -1 : 1) *
        (/right|bottom/.test(baseSide) ? -1 : 1)
      )
      this.setState({
        splitterOffset: offset,
        placeholderOffset: offset
      }, this._triggerUpdate)
    }
  }

  getPaneStyles = (paneId, stateParam, manualOffset) => {
    const styleProp = OFFSET_STYLE_PROPS[this.props.baseSide][paneId]
    return {
      [styleProp]: manualOffset || this.state[stateParam || 'splitterOffset']
    }
  }

  _renderHandle = () => {
    return this.props.snapButtons
      ? <div>
          <div className="grab_handle icon icon-drag-handle" />
          <span className="snap max" onClick={_.partial(this._snap, true)}>
            <span className="icon icon-chevron triangle_arrow up" />
          </span>
          <span className="snap min" onClick={_.partial(this._snap, false)}>
            <span className="icon icon-chevron triangle_arrow down" />
          </span>
        </div>
      : <div className="grab_handle icon icon-drag-handle" />
  }

  render() {
    const { props, state } = this
    const componentClasses = cx(
      {
        pane_splitter_wrap: true,
        //'vertical': props.baseSide === 'left' || props.baseSide === 'right', //defaults to top/horizontal
        active: state.isActive,
        disabled: state.isDisabled
      },
      'base_' + props.baseSide
    )

    return props.detached
      ? <div className={componentClasses}>
          <div
            className="splitter_bar"
            style={this.getPaneStyles('bar', 'placeholderOffset')}
          >
            {this._renderHandle()}
          </div>
          <div
            className="splitter_bar placeholder"
            role="separator"
            tabIndex={0}
            onKeyDown={this._onKeyDown}
            onMouseDown={this.onSplitterClick}
            onTouchStart={this.onSplitterClick}
            style={this.getPaneStyles('bar')}
          >
            <div />
          </div>
        </div>
      : <div className={componentClasses}>
          <div className="split_pane pane_a" style={this.getPaneStyles('a')}>
            {props.children[0]}
          </div>
          <div className="split_pane pane_b" style={this.getPaneStyles('b')}>
            {props.children[1]}
          </div>
          <div
            className="splitter_bar"
            style={this.getPaneStyles('bar', 'placeholderOffset')}
          >
            <div />
          </div>
          <div
            className="splitter_bar placeholder"
            role="separator"
            tabIndex={0}
            onKeyDown={this._onKeyDown}
            onMouseDown={this.onSplitterClick}
            onTouchStart={this.onSplitterClick}
            style={this.getPaneStyles('bar')}
          >
            <div />
          </div>
        </div>
  }
}

export default PaneSplitter
